import { RiLogoutBoxRLine, RiArrowLeftWideLine, RiArrowRightWideLine } from '@remixicon/react';
import i18n from '@/i18n/config';
import { Button } from '@heroui/button';
import { useAtomValue, useSetAtom } from 'jotai';
import { Avatar, AvatarIcon, Tooltip } from '@heroui/react';
import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

import { intlLocaleTag } from '@/lib/locale';
import { roleLabel } from '@/lib/roleLabel';
import { logoutAndRedirect } from '@/utils/auth';
import { profileAtom, sidebarCollapsedAtom, sidebarHoveredAtom } from '@/store';
import { ThemeSwitch } from '@/components/theme-switch';
// import { NotificationBell } from '@/components/notifications/NotificationBell'; // Disabled temporarily

function TopCard() {
  const profile = useAtomValue(profileAtom);
  const isCollapsed = useAtomValue(sidebarCollapsedAtom);
  const setCollapsed = useSetAtom(sidebarCollapsedAtom);
  const setHovered = useSetAtom(sidebarHoveredAtom);
  const [now, setNow] = useState(new Date());
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setNow(new Date());
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  const locale = intlLocaleTag();
  const dateStr = now.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
  const timeStr = now.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit', hour12: false });

  const handleToggleCollapse = () => {
    setCollapsed(!isCollapsed);
  };

  const handleMouseEnter = () => {
    if (isCollapsed) {
      setHovered(true);
      setShowTooltip(true);
    }
  };

  const handleMouseLeave = () => {
    if (isCollapsed) {
      setHovered(false);
      setShowTooltip(false);
    }
  };

  const collapsedContent = (
    <div className="bg-gradient-background-light dark:bg-gradient-background-dark flex flex-col items-center space-y-3 rounded-l-2xl p-3">
      <Avatar icon={<AvatarIcon />} size="sm" />
      <div className="flex flex-col items-center space-y-2">
        <span className="text-center text-xs opacity-50">
          {dateStr} • {timeStr}
        </span>
        <div className="flex flex-col items-center gap-4">
          <ThemeSwitch />
          <Button
            isIconOnly
            color="primary"
            variant="flat"
            aria-label={i18n.t('nav.toggleSidebar')}
            className="-ml-5 self-end rounded-l-none"
            onPress={handleToggleCollapse}
            title={i18n.t('nav.expandSidebar')}
          >
            <RiArrowLeftWideLine className="size-6" />
          </Button>
        </div>
      </div>
    </div>
  );

  const expandedContent = (
    <div className="bg-gradient-background-light dark:bg-gradient-background-dark flex flex-col space-y-4 rounded-l-2xl p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Avatar icon={<AvatarIcon />} />
          <div className="flex flex-col">
            <span>
              {profile?.first_name} {profile?.last_name}
            </span>
            <small className="opacity-50">{roleLabel(profile?.roles[0]?.name)}</small>
          </div>
        </div>

        <Button isIconOnly aria-label={i18n.t('nav.logout')} color="danger" title={i18n.t('nav.logout')} onPress={() => logoutAndRedirect()}>
          <RiLogoutBoxRLine className="size-6" />
        </Button>
      </div>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Button
            isIconOnly
            color="primary"
            variant="flat"
            aria-label={i18n.t('nav.toggleSidebar')}
            className="-ml-4 shrink-0 rounded-l-none"
            onPress={handleToggleCollapse}
            title={i18n.t('nav.collapseSidebar')}
          >
            <RiArrowRightWideLine className="size-6" />
          </Button>
          <ThemeSwitch />
        </div>
        <span className="text-xs opacity-50">
          {dateStr} • {timeStr}
        </span>
      </div>
    </div>
  );

  return (
    <div className="relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      {/* Main Content */}
      <motion.div
        animate={{ width: isCollapsed ? 60 : 256 }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        className="overflow-hidden"
      >
        <AnimatePresence mode="wait">
          {isCollapsed ? (
            <motion.div
              key="collapsed"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              {collapsedContent}
            </motion.div>
          ) : (
            <motion.div
              key="expanded"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              {expandedContent}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Floating Tooltip for Collapsed State */}
      <AnimatePresence>
        {isCollapsed && showTooltip && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8, x: -20 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            exit={{ opacity: 0, scale: 0.8, x: -20 }}
            transition={{ duration: 0.2 }}
            className="pointer-events-none fixed top-4 left-20 z-50"
          >
            <div className="bg-background border-divider rounded-lg border p-4">
              <div className="mb-3 flex items-center gap-3">
                <Avatar icon={<AvatarIcon />} />
                <div className="flex flex-col">
                  <span className="font-medium">
                    {profile?.first_name} {profile?.last_name}
                  </span>
                  <small className="opacity-50">{roleLabel(profile?.roles[0]?.name)}</small>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-xs opacity-50">
                  {dateStr} • {timeStr}
                </span>
                <div className="flex items-center space-x-2">
                  <ThemeSwitch />
                  <Button
                    isIconOnly
                    aria-label={i18n.t('nav.toggleSidebar')}
                    size="sm"
                    variant="light"
                    onPress={handleToggleCollapse}
                    title={i18n.t('nav.expandSidebar')}
                  >
                    <RiArrowLeftWideLine className="size-3" />
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default TopCard;
